import React, { useState, useEffect, useCallback } from 'react';
import { sessionConfigApi } from '../../services/api';
import { SessionConfig, SessionTimeSlotsByDay, SessionTimeSlot } from '../../types';
import { handleApiError, handleSuccess } from '../../utils/errorHandler';
import './SessionConfigManager.css';

const SessionConfigManager: React.FC = () => {
  const [, setConfigs] = useState<SessionConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<SessionConfig | null>(null);
  const [timeSlots, setTimeSlots] = useState<SessionTimeSlotsByDay | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingSlots, setEditingSlots] = useState<SessionTimeSlotsByDay | null>(null);
  const [weeklyTemplate, setWeeklyTemplate] = useState<SessionTimeSlot[]>([]);
  const [activeDays, setActiveDays] = useState<string[]>(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
  const [useWeeklyTemplate, setUseWeeklyTemplate] = useState(true);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const initializeWeeklyTemplate = useCallback((slots: SessionTimeSlotsByDay) => {
    if (!slots) return;
    
    // Get active days
    const activeDaysList = Object.keys(slots).filter(day => 
      slots[day as keyof SessionTimeSlotsByDay]?.length > 0
    );
    setActiveDays(activeDaysList);
    
    // Create template from Monday (or first available day)
    const firstDay = activeDaysList[0];
    if (firstDay && slots[firstDay as keyof SessionTimeSlotsByDay]) {
      const template = slots[firstDay as keyof SessionTimeSlotsByDay].map((slot, index) => ({
        ...slot,
        id: Date.now() + index, // Temporary ID for template
        day_of_week: 'template',
        slot_order: index + 1
      }));
      setWeeklyTemplate(template);
    }
  }, []);

  const loadConfigDetails = useCallback(async (configId: number) => {
    try {
      const response = await sessionConfigApi.getById(configId);
      console.log('Loaded config details:', response.data);
      setSelectedConfig(response.data.config);
      setTimeSlots(response.data.timeSlots);
      
      // Initialize weekly template and active days from loaded data
      initializeWeeklyTemplate(response.data.timeSlots);
    } catch (error) {
      console.error('Failed to load config details:', error);
      handleApiError(error as any, 'Failed to load configuration details');
    }
  }, [initializeWeeklyTemplate]);

  const createFallbackConfig = useCallback(() => {
    const fallbackConfig: SessionConfig = {
      id: 1,
      name: 'Default Schedule',
      description: 'Standard therapy schedule configuration',
      isDefault: true,
      is_active: true
    };

    const fallbackTimeSlots: SessionTimeSlotsByDay = {
      Monday: [
        { id: 1, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Monday', is_break: 0 },
        { id: 2, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Monday', is_break: 0 },
        { id: 3, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Monday', is_break: 0 },
        { id: 4, config_id: 1, session_title: 'Morning Break', start_time: '10:30', end_time: '11:00', slot_order: 4, day_of_week: 'Monday', is_break: 1 },
        { id: 5, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 5, day_of_week: 'Monday', is_break: 0 }
      ],
      Tuesday: [
        { id: 6, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Tuesday', is_break: 0 },
        { id: 7, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Tuesday', is_break: 0 },
        { id: 8, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Tuesday', is_break: 0 },
        { id: 9, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Tuesday', is_break: 0 },
        { id: 10, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Tuesday', is_break: 0 }
      ],
      Wednesday: [
        { id: 11, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Wednesday', is_break: 0 },
        { id: 12, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Wednesday', is_break: 0 },
        { id: 13, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Wednesday', is_break: 0 },
        { id: 14, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Wednesday', is_break: 0 },
        { id: 15, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Wednesday', is_break: 0 }
      ],
      Thursday: [
        { id: 16, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Thursday', is_break: 0 },
        { id: 17, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Thursday', is_break: 0 },
        { id: 18, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Thursday', is_break: 0 },
        { id: 19, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Thursday', is_break: 0 },
        { id: 20, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Thursday', is_break: 0 }
      ],
      Friday: [
        { id: 21, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Friday', is_break: 0 },
        { id: 22, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Friday', is_break: 0 },
        { id: 23, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Friday', is_break: 0 },
        { id: 24, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Friday', is_break: 0 },
        { id: 25, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Friday', is_break: 0 }
      ],
      Saturday: [
        { id: 26, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Saturday', is_break: 0 },
        { id: 27, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Saturday', is_break: 0 },
        { id: 28, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Saturday', is_break: 0 },
        { id: 29, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Saturday', is_break: 0 },
        { id: 30, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Saturday', is_break: 0 }
      ]
    };

    setSelectedConfig(fallbackConfig);
    setTimeSlots(fallbackTimeSlots);
    setConfigs([fallbackConfig]);
    initializeWeeklyTemplate(fallbackTimeSlots);
  }, [initializeWeeklyTemplate]);

  const createDefaultConfig = useCallback(async () => {
    try {
      const defaultTimeSlots: SessionTimeSlotsByDay = {
        Monday: [
          { id: 1, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Monday', is_break: 0 },
          { id: 2, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Monday', is_break: 0 },
          { id: 3, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Monday', is_break: 0 },
          { id: 4, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Monday', is_break: 0 },
          { id: 5, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Monday', is_break: 0 }
        ],
        Tuesday: [
          { id: 6, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 2, day_of_week: 'Tuesday', is_break: 0 },
          { id: 7, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Tuesday', is_break: 0 },
          { id: 8, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Tuesday', is_break: 0 },
          { id: 9, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Tuesday', is_break: 0 },
          { id: 10, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Tuesday', is_break: 0 }
        ],
        Wednesday: [
          { id: 11, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Wednesday', is_break: 0 },
          { id: 12, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Wednesday', is_break: 0 },
          { id: 13, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Wednesday', is_break: 0 },
          { id: 14, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Wednesday', is_break: 0 },
          { id: 15, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Wednesday', is_break: 0 }
        ],
        Thursday: [
          { id: 16, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Thursday', is_break: 0 },
          { id: 17, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Thursday', is_break: 0 },
          { id: 18, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Thursday', is_break: 0 },
          { id: 19, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Thursday', is_break: 0 },
          { id: 20, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Thursday', is_break: 0 }
        ],
        Friday: [
          { id: 21, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Friday', is_break: 0 },
          { id: 22, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Friday', is_break: 0 },
          { id: 23, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Friday', is_break: 0 },
          { id: 24, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Friday', is_break: 0 },
          { id: 25, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Friday', is_break: 0 }
        ],
        Saturday: [
          { id: 26, config_id: 1, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: 'Saturday', is_break: 0 },
          { id: 27, config_id: 1, session_title: 'Period 2', start_time: '09:30', end_time: '10:00', slot_order: 2, day_of_week: 'Saturday', is_break: 0 },
          { id: 28, config_id: 1, session_title: 'Period 3', start_time: '10:00', end_time: '10:30', slot_order: 3, day_of_week: 'Saturday', is_break: 0 },
          { id: 29, config_id: 1, session_title: 'Period 4', start_time: '11:00', end_time: '11:30', slot_order: 4, day_of_week: 'Saturday', is_break: 0 },
          { id: 30, config_id: 1, session_title: 'Period 5', start_time: '11:30', end_time: '12:00', slot_order: 5, day_of_week: 'Saturday', is_break: 0 }
        ]
      };

      const response = await sessionConfigApi.create({
        name: 'Default Schedule',
        description: 'Standard therapy schedule configuration',
        timeSlots: defaultTimeSlots
      });

      setSelectedConfig(response.data.config);
      setTimeSlots(defaultTimeSlots);
      setConfigs([response.data.config]);
      initializeWeeklyTemplate(defaultTimeSlots);
      handleSuccess('Default session configuration created successfully');
    } catch (error) {
      handleApiError(error as any, 'Failed to create default configuration');
      createFallbackConfig();
    }
  }, [createFallbackConfig, initializeWeeklyTemplate]);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await sessionConfigApi.getAll();
      setConfigs(response.data.configs);
      
      // Load default config
      const defaultConfig = response.data.configs.find(c => c.isDefault);
      if (defaultConfig) {
        console.log('Loading default config:', defaultConfig);
        await loadConfigDetails(defaultConfig.id);
      } else if (response.data.configs.length > 0) {
        // Load first available config
        console.log('Loading first available config:', response.data.configs[0]);
        await loadConfigDetails(response.data.configs[0].id);
      } else {
        // No configurations exist, create a default one
        console.log('No configs found, creating default');
        await createDefaultConfig();
      }
    } catch (error) {
      handleApiError(error as any, 'Failed to load session configurations');
      // If API fails, create a basic fallback
      createFallbackConfig();
    } finally {
      setLoading(false);
    }
  }, [loadConfigDetails, createDefaultConfig, createFallbackConfig]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const startEditing = () => {
    if (timeSlots) {
      setEditingSlots(JSON.parse(JSON.stringify(timeSlots)));
      setEditing(true);
      // Initialize weekly template for editing
      initializeWeeklyTemplate(timeSlots);
    }
  };

  const cancelEditing = () => {
    setEditingSlots(null);
    setEditing(false);
    setUseWeeklyTemplate(true);
  };

  const saveChanges = async () => {
    if (!selectedConfig) return;

    // Generate time slots from weekly template if using template mode
    let finalTimeSlots = editingSlots;
    if (useWeeklyTemplate && weeklyTemplate.length > 0) {
      finalTimeSlots = generateTimeSlotsFromTemplate();
    }

    if (!finalTimeSlots) return;

    setLoading(true);
    try {
      await sessionConfigApi.update(selectedConfig.id, {
        timeSlots: finalTimeSlots
      });
      
      setTimeSlots(finalTimeSlots);
      setEditingSlots(null);
      setEditing(false);
      handleSuccess('Session configuration updated successfully');
    } catch (error) {
      handleApiError(error as any, 'Failed to update session configuration');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlotsFromTemplate = (): SessionTimeSlotsByDay => {
    const newTimeSlots: Partial<SessionTimeSlotsByDay> = {};
    let idCounter = Date.now();

    activeDays.forEach(day => {
      newTimeSlots[day as keyof SessionTimeSlotsByDay] = weeklyTemplate.map((templateSlot, index) => ({
        ...templateSlot,
        id: idCounter++,
        config_id: selectedConfig?.id || 0,
        day_of_week: day,
        slot_order: index + 1
      }));
    });

    return newTimeSlots as SessionTimeSlotsByDay;
  };

  const updateTemplateSlot = (slotIndex: number, field: string, value: string) => {
    const updatedTemplate = [...weeklyTemplate];
    updatedTemplate[slotIndex] = {
      ...updatedTemplate[slotIndex],
      [field]: value
    };
    setWeeklyTemplate(updatedTemplate);
  };

  const addTemplateSlot = () => {
    const newSlot: SessionTimeSlot = {
      id: Date.now(),
      config_id: selectedConfig?.id || 0,
      session_title: 'New Period',
      start_time: '09:00',
      end_time: '09:30',
      slot_order: weeklyTemplate.length + 1,
      day_of_week: 'template',
      is_break: 0
    };
    setWeeklyTemplate([...weeklyTemplate, newSlot]);
  };

  const removeTemplateSlot = (slotIndex: number) => {
    setWeeklyTemplate(weeklyTemplate.filter((_, index) => index !== slotIndex));
  };

  const handleActiveDaysChange = (day: string, isActive: boolean) => {
    if (isActive) {
      setActiveDays([...activeDays, day]);
    } else {
      setActiveDays(activeDays.filter(d => d !== day));
    }
  };

  const updateSlot = (day: string, slotIndex: number, field: string, value: string) => {
    if (!editingSlots) return;

    const updatedSlots = { ...editingSlots };
    updatedSlots[day as keyof SessionTimeSlotsByDay][slotIndex] = {
      ...updatedSlots[day as keyof SessionTimeSlotsByDay][slotIndex],
      [field]: value
    };
    setEditingSlots(updatedSlots);
  };

  const addSlot = (day: string) => {
    if (!editingSlots) return;

    const updatedSlots = { ...editingSlots };
    const daySlots = updatedSlots[day as keyof SessionTimeSlotsByDay];
    const newSlot: SessionTimeSlot = {
      id: Date.now(), // Temporary ID
      config_id: selectedConfig?.id || 0,
      session_title: 'New Session',
      start_time: '09:00',
      end_time: '09:30',
      slot_order: daySlots.length + 1,
      day_of_week: day,
      is_break: 0
    };
    
    updatedSlots[day as keyof SessionTimeSlotsByDay] = [...daySlots, newSlot];
    setEditingSlots(updatedSlots);
  };

  const removeSlot = (day: string, slotIndex: number) => {
    if (!editingSlots) return;

    const updatedSlots = { ...editingSlots };
    updatedSlots[day as keyof SessionTimeSlotsByDay] = updatedSlots[day as keyof SessionTimeSlotsByDay].filter((_, index) => index !== slotIndex);
    setEditingSlots(updatedSlots);
  };

  const currentSlots = editing ? editingSlots : timeSlots;

  if (loading && !selectedConfig) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading session configurations...</p>
      </div>
    );
  }

  return (
    <div className="session-config-manager">
      <div className="config-header">
        <h3>Session Configuration</h3>
        <p>Configure session titles, time slots, and schedule structure</p>
      </div>

      {selectedConfig && (
        <div className="config-details">
          <div className="config-info">
            <h4>{selectedConfig.name}</h4>
            {selectedConfig.description && <p>{selectedConfig.description}</p>}
            {selectedConfig.isDefault && <span className="default-badge">Default</span>}
          </div>

          <div className="config-actions">
            {!editing ? (
              <button onClick={startEditing} className="edit-button">
                Edit Configuration
              </button>
            ) : (
              <div className="edit-actions">
                <button onClick={cancelEditing} className="cancel-button">
                  Cancel
                </button>
                <button onClick={saveChanges} className="save-button" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {editing ? (
        <div className="time-slots-configuration">
          <div className="config-section-header">
            <h4>Time Slots Configuration</h4>
            <div className="configuration-mode">
              <label className="mode-toggle">
                <input
                  type="radio"
                  name="configMode"
                  checked={useWeeklyTemplate}
                  onChange={() => setUseWeeklyTemplate(true)}
                />
                <span>Weekly Template (Recommended)</span>
              </label>
              <label className="mode-toggle">
                <input
                  type="radio"
                  name="configMode"
                  checked={!useWeeklyTemplate}
                  onChange={() => setUseWeeklyTemplate(false)}
                />
                <span>Individual Days</span>
              </label>
            </div>
          </div>

          {useWeeklyTemplate ? (
            <div className="weekly-template-config">
              <div className="template-section">
                <div className="section-header">
                  <h5>Weekly Schedule Template</h5>
                  <p>Configure periods once - they'll apply to all selected days</p>
                  <button onClick={addTemplateSlot} className="add-slot-button">
                    + Add Period
                  </button>
                </div>
                
                <div className="template-slots">
                  {weeklyTemplate.map((slot, index) => (
                    <div key={index} className="template-slot-item">
                      <div className="slot-edit">
                        <input
                          type="text"
                          value={slot.session_title}
                          onChange={(e) => updateTemplateSlot(index, 'session_title', e.target.value)}
                          placeholder="Period Title"
                          className="slot-input title-input"
                        />
                        <input
                          type="time"
                          value={slot.start_time}
                          onChange={(e) => updateTemplateSlot(index, 'start_time', e.target.value)}
                          className="slot-input time-input"
                        />
                        <span className="time-separator">-</span>
                        <input
                          type="time"
                          value={slot.end_time}
                          onChange={(e) => updateTemplateSlot(index, 'end_time', e.target.value)}
                          className="slot-input time-input"
                        />
                        <button
                          onClick={() => removeTemplateSlot(index)}
                          className="remove-slot-button"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="active-days-section">
                <h5>Active Days</h5>
                <p>Select which days this schedule applies to</p>
                <div className="weekday-checkboxes">
                  {days.map(day => (
                    <label key={day} className="weekday-checkbox">
                      <input
                        type="checkbox"
                        checked={activeDays.includes(day)}
                        onChange={(e) => handleActiveDaysChange(day, e.target.checked)}
                      />
                      <span>{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              {weeklyTemplate.length > 0 && activeDays.length > 0 && (
                <div className="template-preview">
                  <h5>Preview</h5>
                  <div className="preview-grid">
                    <div className="preview-header">
                      <div className="day-column">Day</div>
                      {weeklyTemplate.map((slot, index) => (
                        <div key={index} className="period-column">
                          <div className="period-title">{slot.session_title}</div>
                          <div className="period-time">{slot.start_time}-{slot.end_time}</div>
                        </div>
                      ))}
                    </div>
                    {activeDays.map(day => (
                      <div key={day} className="preview-row">
                        <div className="day-name">{day}</div>
                        {weeklyTemplate.map((slot, index) => (
                          <div key={index} className="period-cell">
                            <span className="period-indicator">✓</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="individual-days-config">
              <div className="weekday-selector">
                <label>Active Weekdays:</label>
                <div className="weekday-checkboxes">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <label key={day} className="weekday-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(editingSlots && editingSlots[day as keyof SessionTimeSlotsByDay] && editingSlots[day as keyof SessionTimeSlotsByDay].length > 0)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const defaultSlots = [
                              { id: Date.now(), config_id: selectedConfig?.id || 0, session_title: 'Period 1', start_time: '09:00', end_time: '09:30', slot_order: 1, day_of_week: day, is_break: 0 }
                            ];
                            setEditingSlots(prev => {
                              if (!prev) return null;
                              const newSlots = { ...prev };
                              newSlots[day as keyof SessionTimeSlotsByDay] = defaultSlots;
                              return newSlots;
                            });
                          } else {
                            setEditingSlots(prev => {
                              if (!prev) return null;
                              const newSlots = { ...prev };
                              delete newSlots[day as keyof SessionTimeSlotsByDay];
                              return newSlots;
                            });
                          }
                        }}
                      />
                      <span>{day.slice(0, 3)}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {editingSlots && Object.keys(editingSlots).map(day => (
                <div key={day} className="day-section">
                  <div className="day-header">
                    <h5>{day}</h5>
                    <button onClick={() => addSlot(day)} className="add-slot-button">
                      + Add Slot
                    </button>
                  </div>
                  
                  <div className="slots-list">
                    {editingSlots[day as keyof SessionTimeSlotsByDay]?.map((slot, index) => (
                      <div key={index} className="slot-item">
                        <div className="slot-edit">
                          <input
                            type="text"
                            value={slot.session_title}
                            onChange={(e) => updateSlot(day, index, 'session_title', e.target.value)}
                            placeholder="Session Title"
                            className="slot-input title-input"
                          />
                          <input
                            type="time"
                            value={slot.start_time}
                            onChange={(e) => updateSlot(day, index, 'start_time', e.target.value)}
                            className="slot-input time-input"
                          />
                          <span className="time-separator">-</span>
                          <input
                            type="time"
                            value={slot.end_time}
                            onChange={(e) => updateSlot(day, index, 'end_time', e.target.value)}
                            className="slot-input time-input"
                          />
                          <button
                            onClick={() => removeSlot(day, index)}
                            className="remove-slot-button"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        currentSlots && (
          <div className="time-slots-display">
            <div className="config-section-header">
              <h4>Current Schedule</h4>
            </div>
            
            <div className="schedule-overview">
              <div className="active-days-summary">
                <strong>Active Days:</strong> {Object.keys(currentSlots).join(', ')}
              </div>
              
              <div className="periods-summary">
                {Object.keys(currentSlots).length > 0 && (
                  <div className="periods-grid">
                    <div className="periods-header">
                      <div className="day-column">Day</div>
                      {currentSlots[Object.keys(currentSlots)[0] as keyof SessionTimeSlotsByDay]?.map((slot, index) => (
                        <div key={index} className="period-column">
                          <div className="period-title">{slot.session_title}</div>
                          <div className="period-time">{slot.start_time} - {slot.end_time}</div>
                        </div>
                      ))}
                    </div>
                    {Object.keys(currentSlots).map(day => (
                      <div key={day} className="periods-row">
                        <div className="day-name">{day}</div>
                        {currentSlots[day as keyof SessionTimeSlotsByDay]?.map((slot, index) => (
                          <div key={index} className="period-cell">
                            <span className="slot-title">{slot.session_title}</span>
                            <span className="slot-time">{slot.start_time} - {slot.end_time}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default SessionConfigManager;